import React, { useState, useEffect } from 'react';
import { 
  UserPlus, Phone, Mail, MapPin, Calendar, Clock,
  CheckCircle2, XCircle, MessageSquare, ChevronRight,
  Facebook, Globe, Sparkles, Filter, Search, MoreVertical,
  User, Briefcase, ArrowRight, Plus, Trash2, Edit3,
  AlertCircle, Star, TrendingUp, Zap, ChevronDown, ChevronUp, X, HelpCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import * as api from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Lead status configuration
const LEAD_STATUSES = {
  new: { label: 'New', color: 'bg-blue-100 text-blue-700', icon: Sparkles },
  contacted: { label: 'Contacted', color: 'bg-amber-100 text-amber-700', icon: Phone },
  quoted: { label: 'Quoted', color: 'bg-purple-100 text-purple-700', icon: Briefcase },
  won: { label: 'Won', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  lost: { label: 'Lost', color: 'bg-red-100 text-red-700', icon: XCircle }
};

// Lead source icons
const SOURCE_ICONS = {
  facebook_lead_ad: { icon: Facebook, color: 'text-blue-600', bg: 'bg-blue-100' },
  google_lead_form: { icon: Globe, color: 'text-red-500', bg: 'bg-red-100' },
  website: { icon: Globe, color: 'text-emerald-600', bg: 'bg-emerald-100' },
  referral: { icon: UserPlus, color: 'text-purple-600', bg: 'bg-purple-100' },
  manual: { icon: Plus, color: 'text-zinc-600', bg: 'bg-zinc-100' }
};

// Format relative time
const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

// Facebook Lead Ads Connector Component
const FacebookLeadAdsConnector = ({ expanded, onToggle }) => {
  const [showSetupHelp, setShowSetupHelp] = useState(false);
  
  return (
    <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden mx-4 mb-4">
      <button 
        onClick={onToggle}
        className="w-full p-4 flex items-center justify-between hover:bg-zinc-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
            <Facebook size={20} className="text-white" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-zinc-900">Facebook Lead Ads</h3>
            <p className="text-sm text-zinc-500">Get new customer enquiries from Facebook</p>
          </div>
        </div>
        {expanded ? <ChevronUp size={20} className="text-zinc-400" /> : <ChevronDown size={20} className="text-zinc-400" />}
      </button>
      
      {expanded && (
        <div className="p-4 pt-0 space-y-4 border-t border-zinc-100">
          {/* Simple explanation */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4">
            <h4 className="font-medium text-zinc-900 mb-3">What is this?</h4>
            <p className="text-sm text-zinc-600 mb-3">
              If you run Facebook ads to get new customers, their details can automatically appear here when they fill out your ad form. No more copying and pasting!
            </p>
            <div className="flex items-center gap-2 text-sm text-blue-700 font-medium">
              <Sparkles size={16} />
              <span>New enquiries appear instantly</span>
            </div>
          </div>
          
          {/* Setup help */}
          {!showSetupHelp ? (
            <button 
              onClick={() => setShowSetupHelp(true)}
              className="w-full flex items-center justify-between p-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors group"
            >
              <div className="flex items-center gap-3">
                <Facebook size={20} />
                <span className="font-medium">Set Up Facebook Leads</span>
              </div>
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </button>
          ) : (
            <div className="bg-zinc-50 rounded-xl p-4 space-y-4">
              <h4 className="font-medium text-zinc-900 flex items-center gap-2">
                <HelpCircle size={18} className="text-blue-600" />
                How to connect your Facebook ads
              </h4>
              <div className="text-sm text-zinc-600 space-y-3">
                <p>To get Facebook leads flowing into StreakFree, we need to set this up together. It takes about 10 minutes.</p>
                <div className="bg-white rounded-lg p-3 border border-zinc-200">
                  <p className="font-medium text-zinc-800 mb-2">Contact us and we'll help you:</p>
                  <ul className="space-y-1 text-zinc-600">
                    <li>• Connect your Facebook page</li>
                    <li>• Link your Lead Ads</li>
                    <li>• Test everything works</li>
                  </ul>
                </div>
                <a 
                  href="mailto:support@streakfree.app?subject=Help%20connecting%20Facebook%20Lead%20Ads"
                  className="flex items-center justify-center gap-2 w-full p-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  <Mail size={18} />
                  Email Us for Help
                </a>
                <button
                  onClick={() => setShowSetupHelp(false)}
                  className="w-full p-2 text-zinc-500 text-sm"
                >
                  Back
                </button>
              </div>
            </div>
          )}
          
          {/* Google - coming soon */}
          <button 
            className="w-full flex items-center justify-between p-4 bg-zinc-100 rounded-xl opacity-60 cursor-not-allowed"
            disabled
          >
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 bg-gradient-to-br from-red-500 via-yellow-500 to-green-500 rounded flex items-center justify-center">
                <Globe size={14} className="text-white" />
              </div>
              <span className="font-medium text-zinc-700">Google Lead Forms</span>
            </div>
            <span className="px-2 py-1 bg-zinc-200 text-zinc-600 text-xs rounded-full">Coming Soon</span>
          </button>
        </div>
      )}
    </div>
  );
};

// Lead Card Component
const LeadCard = ({ lead, onSelect, onStatusChange, onConvert }) => {
  const status = LEAD_STATUSES[lead.status] || LEAD_STATUSES.new;
  const source = SOURCE_ICONS[lead.source] || SOURCE_ICONS.manual;
  const StatusIcon = status.icon;
  const SourceIcon = source.icon;
  
  return (
    <div 
      className="bg-white rounded-xl border border-zinc-200 p-4 hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => onSelect(lead)}
      data-testid={`lead-card-${lead.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 ${source.bg} rounded-lg flex items-center justify-center`}>
            <SourceIcon size={20} className={source.color} />
          </div>
          <div>
            <h3 className="font-semibold text-zinc-900">{lead.name || 'Unknown'}</h3>
            <p className="text-xs text-zinc-500">{formatRelativeTime(lead.created_at)}</p>
          </div>
        </div>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
          {status.label}
        </span>
      </div>
      
      <div className="space-y-1.5 text-sm">
        {lead.phone && (
          <div className="flex items-center gap-2 text-zinc-600">
            <Phone size={14} />
            <span>{lead.phone}</span>
          </div>
        )}
        {lead.email && (
          <div className="flex items-center gap-2 text-zinc-600">
            <Mail size={14} />
            <span className="truncate">{lead.email}</span>
          </div>
        )}
        {lead.address && (
          <div className="flex items-center gap-2 text-zinc-600">
            <MapPin size={14} />
            <span className="truncate">{lead.address}</span>
          </div>
        )}
      </div>
      
      {lead.notes && (
        <p className="mt-3 text-xs text-zinc-500 bg-zinc-50 rounded-lg p-2 line-clamp-2">
          {lead.notes}
        </p>
      )}
      
      {lead.status !== 'won' && lead.status !== 'lost' && (
        <div className="mt-3 pt-3 border-t border-zinc-100 flex gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); onConvert(lead); }}
            className="flex-1 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1"
          >
            <UserPlus size={14} />
            Add to Customers
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onStatusChange(lead, 'lost'); }}
            className="px-3 py-2 bg-zinc-100 hover:bg-zinc-200 text-zinc-600 text-xs rounded-lg transition-colors"
          >
            <XCircle size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

// Lead Detail Modal
const LeadDetailModal = ({ lead, onClose, onStatusChange, onConvert, onDelete }) => {
  const status = LEAD_STATUSES[lead.status] || LEAD_STATUSES.new;
  const source = SOURCE_ICONS[lead.source] || SOURCE_ICONS.manual;
  const SourceIcon = source.icon;
  
  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-zinc-100">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 ${source.bg} rounded-xl flex items-center justify-center`}>
                <SourceIcon size={24} className={source.color} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-zinc-900">{lead.name}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                    {status.label}
                  </span>
                  <span className="text-xs text-zinc-500">
                    {lead.source === 'facebook_lead_ad' ? 'From Facebook' : 
                     lead.source === 'google_lead_form' ? 'From Google' : 'Manual entry'}
                  </span>
                </div>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded-lg">
              <XCircle size={20} className="text-zinc-400" />
            </button>
          </div>
        </div>
        
        {/* Contact Info */}
        <div className="p-6 space-y-4">
          <div className="space-y-3">
            {lead.phone && (
              <a 
                href={`tel:${lead.phone}`}
                className="flex items-center gap-3 p-3 bg-zinc-50 hover:bg-zinc-100 rounded-xl transition-colors"
              >
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Phone size={18} className="text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Phone</p>
                  <p className="font-medium text-zinc-900">{lead.phone}</p>
                </div>
              </a>
            )}
            
            {lead.email && (
              <a 
                href={`mailto:${lead.email}`}
                className="flex items-center gap-3 p-3 bg-zinc-50 hover:bg-zinc-100 rounded-xl transition-colors"
              >
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Mail size={18} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Email</p>
                  <p className="font-medium text-zinc-900 truncate">{lead.email}</p>
                </div>
              </a>
            )}
            
            {lead.address && (
              <div className="flex items-center gap-3 p-3 bg-zinc-50 rounded-xl">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <MapPin size={18} className="text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Address</p>
                  <p className="font-medium text-zinc-900">{lead.address}</p>
                </div>
              </div>
            )}
          </div>
          
          {lead.notes && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <p className="text-sm font-medium text-amber-900 mb-1">Notes from ad</p>
              <p className="text-sm text-amber-800">{lead.notes}</p>
            </div>
          )}
          
          <div className="text-xs text-zinc-500 flex items-center gap-2">
            <Clock size={12} />
            Lead received: {new Date(lead.created_at).toLocaleString('en-GB')}
          </div>
        </div>
        
        {/* Status Actions */}
        {lead.status !== 'won' && lead.status !== 'lost' && (
          <div className="p-6 border-t border-zinc-100 bg-zinc-50">
            <p className="text-sm font-medium text-zinc-700 mb-3">Update status</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(LEAD_STATUSES).map(([key, config]) => {
                if (key === 'won') return null; // Won is handled by convert button
                const Icon = config.icon;
                return (
                  <button
                    key={key}
                    onClick={() => onStatusChange(lead, key)}
                    className={`px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors ${
                      lead.status === key 
                        ? config.color + ' ring-2 ring-offset-1'
                        : 'bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-50'
                    }`}
                  >
                    <Icon size={14} />
                    {config.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Actions */}
        <div className="p-6 border-t border-zinc-100 space-y-3">
          {lead.status !== 'won' && lead.status !== 'lost' && (
            <Button
              onClick={() => onConvert(lead)}
              className="w-full bg-emerald-600 hover:bg-emerald-700 py-3"
            >
              <UserPlus size={18} className="mr-2" />
              Convert to Customer
            </Button>
          )}
          
          {lead.status === 'won' && (
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-center">
              <CheckCircle2 size={24} className="text-emerald-600 mx-auto mb-2" />
              <p className="font-medium text-emerald-900">Lead converted!</p>
              <p className="text-sm text-emerald-700">Added to your customers</p>
            </div>
          )}
          
          {lead.status === 'lost' && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-center">
              <XCircle size={24} className="text-red-500 mx-auto mb-2" />
              <p className="font-medium text-red-900">Lead marked as lost</p>
              <button 
                onClick={() => onStatusChange(lead, 'new')}
                className="text-sm text-red-700 underline mt-1"
              >
                Reopen lead
              </button>
            </div>
          )}
          
          <button
            onClick={() => onDelete(lead)}
            className="w-full py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm flex items-center justify-center gap-2"
          >
            <Trash2 size={16} />
            Delete lead
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Leads Page
const Leads = ({ onNavigate }) => {
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFacebookConnector, setShowFacebookConnector] = useState(true);
  const [showNewLeadForm, setShowNewLeadForm] = useState(false);
  
  // Fetch leads
  const loadLeads = async () => {
    setIsLoading(true);
    try {
      const token = api.getToken();
      const res = await fetch(`${API_URL}/api/leads`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setLeads(data);
      }
    } catch (err) {
      console.error('Error loading leads:', err);
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    loadLeads();
    
    // Check if we should open create form
    const openCreate = sessionStorage.getItem('openCreateForm');
    if (openCreate === 'true') {
      setShowNewLeadForm(true);
      sessionStorage.removeItem('openCreateForm');
    }
  }, []);

  // "+ New → New Lead" from the global header — opens the form even if we're
  // already on this page.
  useEffect(() => {
    const handler = (e) => {
      if (e?.detail?.action !== 'leads') return;
      sessionStorage.removeItem('openCreateForm');
      setShowNewLeadForm(true);
    };
    window.addEventListener('streakfree:open-create-form', handler);
    return () => window.removeEventListener('streakfree:open-create-form', handler);
  }, []);
  
  // Update lead status
  const handleStatusChange = async (lead, newStatus) => {
    try {
      const token = api.getToken();
      await fetch(`${API_URL}/api/leads/${lead.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      setLeads(leads.map(l => l.id === lead.id ? { ...l, status: newStatus } : l));
      if (selectedLead?.id === lead.id) {
        setSelectedLead({ ...selectedLead, status: newStatus });
      }
    } catch (err) {
      console.error('Error updating lead:', err);
    }
  };
  
  // Convert lead to customer
  const handleConvert = async (lead) => {
    try {
      const token = api.getToken();
      const res = await fetch(`${API_URL}/api/leads/${lead.id}/convert`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        // Update status to won
        handleStatusChange(lead, 'won');
        // Show success and close modal after delay
        setTimeout(() => {
          setSelectedLead(null);
          loadLeads();
        }, 1500);
      }
    } catch (err) {
      console.error('Error converting lead:', err);
    }
  };
  
  // Delete lead
  const handleDelete = async (lead) => {
    if (!window.confirm('Delete this lead? This cannot be undone.')) return;
    
    try {
      const token = api.getToken();
      await fetch(`${API_URL}/api/leads/${lead.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setLeads(leads.filter(l => l.id !== lead.id));
      setSelectedLead(null);
    } catch (err) {
      console.error('Error deleting lead:', err);
    }
  };
  
  // Filter leads
  const filteredLeads = leads.filter(lead => {
    const matchesStatus = filterStatus === 'all' || lead.status === filterStatus;
    const matchesSearch = !searchQuery || 
      lead.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.phone?.includes(searchQuery);
    return matchesStatus && matchesSearch;
  });
  
  // Group leads by status for summary
  const statusCounts = leads.reduce((acc, lead) => {
    acc[lead.status] = (acc[lead.status] || 0) + 1;
    return acc;
  }, {});
  
  return (
    <div className="h-full flex flex-col bg-zinc-50" data-testid="leads-page">
      {/* Header */}
      <div className="bg-white border-b border-zinc-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">Leads</h1>
            <p className="text-sm text-zinc-500">Manage enquiries from ads and website</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-2xl font-bold text-emerald-600">{leads.length}</p>
              <p className="text-xs text-zinc-500">Total leads</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Stats Bar */}
      <div className="bg-white border-b border-zinc-200 px-6 py-3">
        <div className="flex items-center gap-4 overflow-x-auto">
          <button
            onClick={() => setFilterStatus('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              filterStatus === 'all' 
                ? 'bg-zinc-900 text-white' 
                : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
            }`}
          >
            All ({leads.length})
          </button>
          {Object.entries(LEAD_STATUSES).map(([key, config]) => {
            const count = statusCounts[key] || 0;
            const Icon = config.icon;
            return (
              <button
                key={key}
                onClick={() => setFilterStatus(key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-2 ${
                  filterStatus === key 
                    ? config.color + ' ring-2 ring-offset-1' 
                    : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                }`}
              >
                <Icon size={14} />
                {config.label} ({count})
              </button>
            );
          })}
        </div>
      </div>
      
      {/* Search */}
      <div className="px-6 py-4">
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            placeholder="Search leads..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>
      
      {/* Facebook Lead Ads Connector */}
      <FacebookLeadAdsConnector 
        expanded={showFacebookConnector}
        onToggle={() => setShowFacebookConnector(!showFacebookConnector)}
      />
      
      {/* Leads Grid */}
      <div className="flex-1 overflow-y-auto px-6 pb-6">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredLeads.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <div className="w-16 h-16 bg-zinc-200 rounded-2xl flex items-center justify-center mb-4">
              <TrendingUp size={28} className="text-zinc-400" />
            </div>
            <h3 className="font-semibold text-zinc-900 mb-1">
              {leads.length === 0 ? 'No leads yet' : 'No matching leads'}
            </h3>
            <p className="text-sm text-zinc-500 max-w-xs">
              {leads.length === 0 
                ? 'Leads from Facebook ads will appear here automatically' 
                : 'Try adjusting your filters'}
            </p>
            {leads.length === 0 && (
              <button
                onClick={() => onNavigate && onNavigate('api-docs')}
                className="mt-4 text-sm text-emerald-600 hover:text-emerald-700 font-medium"
              >
                Set up Facebook Lead Ads →
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredLeads.map(lead => (
              <LeadCard
                key={lead.id}
                lead={lead}
                onSelect={setSelectedLead}
                onStatusChange={handleStatusChange}
                onConvert={handleConvert}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Lead Detail Modal */}
      {selectedLead && (
        <LeadDetailModal
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onStatusChange={handleStatusChange}
          onConvert={handleConvert}
          onDelete={handleDelete}
        />
      )}
      
      {/* FAB - Add New Lead */}
      <button 
        onClick={() => setShowNewLeadForm(true)}
        className="fixed bottom-20 right-6 w-14 h-14 bg-emerald-500 rounded-full shadow-lg flex items-center justify-center text-white hover:bg-emerald-600 transition-colors z-30"
        data-testid="add-lead-fab"
      >
        <Plus size={24} />
      </button>
      
      {/* New Lead Modal */}
      {showNewLeadForm && (
        <NewLeadModal
          onClose={() => setShowNewLeadForm(false)}
          onSave={(newLead) => {
            loadLeads();
            setShowNewLeadForm(false);
          }}
        />
      )}
    </div>
  );
};

// New Lead Modal Component
const NewLeadModal = ({ onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    source: 'manual',
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    
    setSaving(true);
    try {
      const token = api.getToken();
      const res = await fetch(`${API_URL}/api/leads`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          status: 'new'
        })
      });
      
      if (res.ok) {
        const newLead = await res.json();
        onSave(newLead);
      }
    } catch (err) {
      console.error('Error creating lead:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-zinc-100 flex items-center justify-between">
          <h3 className="text-lg font-bold text-zinc-900">Add New Lead</h3>
          <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded-lg">
            <X size={20} className="text-zinc-500" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full px-4 py-3 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="Lead name"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="w-full px-4 py-3 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="email@example.com"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              className="w-full px-4 py-3 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="Phone number"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1">Source</label>
            <select
              value={formData.source}
              onChange={(e) => setFormData({...formData, source: e.target.value})}
              className="w-full px-4 py-3 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="manual">Manual Entry</option>
              <option value="facebook">Facebook</option>
              <option value="website">Website</option>
              <option value="referral">Referral</option>
              <option value="phone">Phone Call</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              className="w-full px-4 py-3 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
              rows={3}
              placeholder="Additional notes..."
            />
          </div>
          
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-zinc-200 rounded-xl text-zinc-700 font-medium hover:bg-zinc-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !formData.name.trim()}
              className="flex-1 px-4 py-3 bg-emerald-500 rounded-xl text-white font-medium hover:bg-emerald-600 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Add Lead'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Leads;
